import type { ReactNode } from "react";
import { BotIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { activityTypography } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import type { User } from "@/types";

// Colored icon circle at the left edge of an activity row.
export const EventIconCircle = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <Row
      className={cn(
        "h-6 w-6 shrink-0 items-center justify-center rounded-full",
        className,
      )}
    >
      {children}
    </Row>
  );
};

// The acting staffer's name, shown as "You" when it's the viewer.
export const UserName = ({
  currentUserId,
  user,
}: {
  currentUserId: number;
  user?: User;
}) => {
  const name = user ? `${user.firstName} ${user.lastName}` : "Staffer";

  return (
    <span className={activityTypography.emphasis}>
      {user?.id === currentUserId ? "You" : name}
    </span>
  );
};

// Quote block under an activity sentence (reasons, comments), marked by a
// rounded bar along the left edge.
export const ActivityQuote = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        "relative pl-4 before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-full before:bg-slate-200",
        className,
      )}
    >
      {children}
    </div>
  );
};

// An activity row's relative time, with the exact date and time on hover.
export const ActivityTimestamp = ({ createdAt }: { createdAt: string }) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        {/* Row instead of Text because Radix asChild needs a ref-forwarding trigger. */}
        <TooltipTrigger asChild>
          <Row className="w-fit pt-1">
            <Text className={activityTypography.meta} variant="muted">
              {dates.timeAgoForPipeline(createdAt)} ·{" "}
              {dates.formatShortDate(createdAt)}
            </Text>
          </Row>
        </TooltipTrigger>
        <TooltipContent className="rounded-md border-transparent bg-slate-800 px-2 py-1 text-xs font-medium text-slate-50 shadow-sm">
          {dates.formatShortDateTime(createdAt)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Shared skeleton of an activity row: icon with optional badges, sentence,
// optional quote content, and the timestamp.
export const ActivityEventRow = ({
  byAgent = false,
  children,
  createdAt,
  icon,
  quote,
}: {
  byAgent?: boolean;
  children: ReactNode;
  createdAt: string;
  icon: ReactNode;
  quote?: ReactNode;
}) => {
  /* Render */
  const render = () => {
    return (
      <Row className="gap-2">
        <div className="relative">
          {icon}
          {byAgent ? (
            <span className="absolute -right-[7px] -top-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-purple-100 text-purple-600 ring-1 ring-white">
              <BotIcon className="h-2 w-2" />
            </span>
          ) : null}
        </div>
        <Column className="gap-0.5">
          <Text className={activityTypography.body} variant="secondary">
            {children}
          </Text>
          {quote}
          <ActivityTimestamp createdAt={createdAt} />
        </Column>
      </Row>
    );
  };

  return render();
};
